import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { RestApiError, restClient } from "../../../../lib/rest-client";

type ContractWithTalent = {
  id: string;
  talentId: string;
  talentName: string;
  clientCompany: string | null;
  projectName: string | null;
  contractType: string;
  reward: number | null;
  margin: number | null;
  startDate: string;
  endDate: string | null;
  status: string;
  renewalCount: number;
  createdAt: string;
};

type ContractListResponse = {
  items: ContractWithTalent[];
  total: number;
  page: number;
  limit: number;
};

/**
 * 残日数を計算する
 * @param {string | null} endDate 契約終了日
 * @return {number | null} 残日数（nullは終了日未設定）
 */
function calcDaysRemaining(endDate: string | null): number | null {
  if (!endDate) return null;
  const diff = new Date(endDate).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

/**
 * 残日数に応じたテキストクラスを返す
 * @param {number | null} days 残日数
 * @return {string} CSSクラス
 */
function getDaysRemainingClass(days: number | null): string {
  if (days === null) return "text-base-content/60";
  if (days <= 14) return "text-error font-bold";
  if (days <= 30) return "text-warning font-bold";
  return "text-success";
}

/**
 * 契約ステータスのラベルを返す
 * @param {string} status ステータス値
 * @return {object} label, badgeClass
 */
function getContractStatusBadge(status: string): {
  label: string;
  badgeClass: string;
} {
  switch (status) {
    case "active":
      return { label: "稼働中", badgeClass: "badge-success" };
    case "renewal_pending":
      return { label: "更新待ち", badgeClass: "badge-warning" };
    case "ended":
      return { label: "終了", badgeClass: "badge-ghost" };
    case "renewed":
      return { label: "更新済", badgeClass: "badge-info" };
    default:
      return { label: status, badgeClass: "badge-ghost" };
  }
}

type ContractTabProps = {
  canManageContracts: boolean;
};

/**
 * 契約タブコンポーネント
 * 全タレント横断の契約一覧をAPIから取得して表示する
 * @param {object} props プロパティ
 * @param {boolean} props.canManageContracts 契約管理権限（falseの場合、単価・マージンをマスクする）
 * @return {React.ReactElement} 契約タブ
 */
export const ContractTab: React.FC<ContractTabProps> = ({
  canManageContracts,
}) => {
  const [contracts, setContracts] = useState<ContractWithTalent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [page, setPage] = useState(1);
  const limit = 50;

  useEffect(() => {
    let cancelled = false;

    const fetchContracts = async () => {
      setLoading(true);
      setError(null);
      try {
        const params: Record<string, string> = {
          page: String(page),
          limit: String(limit),
        };
        if (statusFilter) {
          params.status = statusFilter;
        }
        const res = await restClient.get<ContractListResponse>(
          "/talents/contracts",
          params,
        );
        if (!cancelled) {
          setContracts(res.items);
          setTotal(res.total);
        }
      } catch (e) {
        if (!cancelled) {
          const msg =
            e instanceof RestApiError
              ? e.message
              : "契約データの取得に失敗しました";
          setError(msg);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchContracts().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [page, statusFilter]);

  const handleStatusFilterChange = useCallback((value: string) => {
    setStatusFilter(value);
    setPage(1);
  }, []);

  const totalPages = Math.ceil(total / limit);

  if (error) {
    return (
      <div className="alert alert-error">
        <span>{error}</span>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  return (
    <div>
      {/* Filter */}
      <div className="flex items-center gap-3 mb-4">
        <select
          className="select select-bordered select-sm"
          value={statusFilter}
          onChange={e => handleStatusFilterChange(e.target.value)}
        >
          <option value="">全ステータス</option>
          <option value="active">稼働中</option>
          <option value="renewal_pending">更新待ち</option>
          <option value="ended">終了</option>
          <option value="renewed">更新済</option>
        </select>
        <span className="text-sm text-base-content/60">{total}件</span>
      </div>

      {/* Table */}
      <div className="card bg-base-100 shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>タレント</th>
                <th>契約先</th>
                <th>案件名</th>
                <th>単価</th>
                <th>マージン</th>
                <th>契約期間</th>
                <th>残日数</th>
                <th>ステータス</th>
                <th>更新回数</th>
              </tr>
            </thead>
            <tbody>
              {contracts.map(contract => {
                const daysRemaining = calcDaysRemaining(contract.endDate);
                const statusBadge = getContractStatusBadge(contract.status);
                const daysClass = getDaysRemainingClass(daysRemaining);
                return (
                  <tr key={contract.id} className="hover">
                    <td className="font-medium">{contract.talentName}</td>
                    <td>{contract.clientCompany || "-"}</td>
                    <td>{contract.projectName || "-"}</td>
                    <td>
                      {canManageContracts
                        ? contract.reward != null
                          ? `${contract.reward}万`
                          : "-"
                        : "---"}
                    </td>
                    <td>
                      {canManageContracts
                        ? contract.margin != null
                          ? `${contract.margin}万`
                          : "-"
                        : "---"}
                    </td>
                    <td className="text-xs">
                      {contract.startDate}
                      {contract.endDate ? ` ~ ${contract.endDate}` : " ~"}
                    </td>
                    <td>
                      <span className={daysClass}>
                        {daysRemaining !== null ? `${daysRemaining}日` : "-"}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`badge badge-sm ${statusBadge.badgeClass}`}
                      >
                        {statusBadge.label}
                      </span>
                    </td>
                    <td>{contract.renewalCount}</td>
                  </tr>
                );
              })}
              {contracts.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    className="text-center py-8 text-base-content/60"
                  >
                    契約が見つかりません
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-4">
          <div className="join">
            <button
              type="button"
              className="join-item btn btn-sm"
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
            >
              «
            </button>
            <button type="button" className="join-item btn btn-sm">
              {page} / {totalPages}
            </button>
            <button
              type="button"
              className="join-item btn btn-sm"
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              »
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
